import { Component, signal, Signal } from '@angular/core';
import { CommonModule, DatePipe, CurrencyPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BillingService } from '../../services/billing.service';
import { PatientService } from '../../services/patient.service';
import { Bill } from '../../models/billing.model';
import { Patient } from '../../models/patient.model';
import { ButtonComponent, InputComponent } from '../ui';

@Component({
  selector: 'app-billing',
  imports: [CommonModule, FormsModule, DatePipe, CurrencyPipe, ButtonComponent, InputComponent],
  templateUrl: './billing.component.html',
  styleUrl: './billing.component.css'
})
export class BillingComponent {
  bills!: Signal<Bill[]>;
  patients!: Signal<Patient[]>;
  showModal = signal(false);
  editingBill = signal<Bill | null>(null);
  billForm = signal<Partial<Bill>>({});

  constructor(
    private billingService: BillingService,
    private patientService: PatientService
  ) {
    this.bills = this.billingService.getBills();
    this.patients = this.patientService.getPatients();
  }

  openAddModal() {
    this.editingBill.set(null);
    this.billForm.set({
      patientId: '',
      patientName: '',
      billNumber: `BL-${new Date().getFullYear()}-${String(this.bills().length + 1).padStart(3, '0')}`,
      date: new Date(),
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
      items: [{
        description: '',
        quantity: 1,
        unitPrice: 0,
        total: 0
      }],
      subtotal: 0,
      tax: 0,
      discount: 0,
      total: 0,
      status: 'Pending'
    });
    this.showModal.set(true);
  }

  openEditModal(bill: Bill) {
    this.editingBill.set(bill);
    this.billForm.set({ ...bill });
    this.showModal.set(true);
  }

  closeModal() {
    this.showModal.set(false);
    this.editingBill.set(null);
  }

  onPatientSelect(patientId: string) {
    const patient = this.patients().find((p: Patient) => p.id === patientId);
    if (patient) {
      this.billForm().patientName = `${patient.firstName} ${patient.lastName}`;
    }
  }

  addBillItem() {
    const items = this.billForm().items || [];
    items.push({
      description: '',
      quantity: 1,
      unitPrice: 0,
      total: 0
    });
    this.billForm().items = items;
    this.calculateTotal();
  }

  removeBillItem(index: number) {
    const items = this.billForm().items || [];
    items.splice(index, 1);
    this.billForm().items = items;
    this.calculateTotal();
  }

  calculateTotal() {
    const items = this.billForm().items || [];
    items.forEach(item => {
      item.total = item.quantity * item.unitPrice;
    });
    const subtotal = items.reduce((sum, item) => sum + item.total, 0);
    const tax = subtotal * 0.1; // 10% tax
    const discount = this.billForm().discount || 0;
    const total = subtotal + tax - discount;
    
    this.billForm().subtotal = subtotal;
    this.billForm().tax = tax;
    this.billForm().total = total;
  }

  saveBill() {
    const form = this.billForm();
    if (this.editingBill()) {
      this.billingService.updateBill(this.editingBill()!.id, form);
    } else {
      this.billingService.addBill(form as Omit<Bill, 'id' | 'createdAt' | 'updatedAt'>);
    }
    this.closeModal();
  }

  deleteBill(id: string) {
    if (confirm('Are you sure you want to delete this bill?')) {
      this.billingService.deleteBill(id);
    }
  }

  onDateChange(value: string) {
    this.billForm().date = new Date(value);
  }

  onDueDateChange(value: string) {
    this.billForm().dueDate = new Date(value);
  }

  markAsPaid(bill: Bill) {
    this.billingService.markAsPaid(bill.id, 'Cash', new Date());
  }
}

